/**
 * Unified Work Order + Invoice Status DISPLAY constants.
 *
 * Canonical PRESENTATION layer (labels + colors) for statuses that customers and
 * contractors both see, shared by:
 *   - apps/contractor-app/  (re-exported by src/constants/statusEnums.js — that file
 *                            keeps owning the TRANSITION maps, which are validated
 *                            against the DB function by check-status-transition-drift)
 *   - apps/customer-portal/ (imports from '@tradesmatepro/shared-ui' directly)
 *
 * Why this exists: the customer portal is a separate app and could not import the
 * contractor app's statusEnums.js, so it hand-rolled four partial label maps. Customers
 * were shown raw DB tokens like `changes_requested` and `Partially_paid` on the public
 * quote page and the portal invoice list. One source of truth for labels fixes all of them.
 *
 * MUST match the live DB enums. To verify:
 *   node db-query.js "SELECT t.typname, e.enumlabel FROM pg_type t JOIN pg_enum e ON t.oid=e.enumtypid WHERE t.typname IN ('work_order_status_enum','invoice_status_enum') ORDER BY t.typname, e.enumsortorder"
 *
 * work_order_status_enum (verified live 2026-08-02): draft, sent, presented,
 *   changes_requested, follow_up, approved, rejected, expired, scheduled, in_progress,
 *   completed, on_hold, invoiced, paid, closed, cancelled, needs_rescheduling
 * invoice_status_enum (verified live 2026-08-02): draft, sent, viewed, partially_paid,
 *   paid, overdue, disputed, written_off, cancelled, void, refunded, partially_refunded
 */

// ========================================
// WORK ORDER STATUS (quote + job pipeline)
// ========================================

export const WORK_ORDER_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  presented: 'Presented',
  changes_requested: 'Changes Requested',
  follow_up: 'Follow-up Needed',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
  cancelled: 'Cancelled',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  needs_rescheduling: 'Needs Rescheduling',
  completed: 'Completed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  closed: 'Closed'
};

export const WORK_ORDER_STATUS_COLORS = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  sent: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  presented: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  changes_requested: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  follow_up: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  approved: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  scheduled: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  on_hold: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  needs_rescheduling: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  invoiced: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  paid: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' }
};

// ========================================
// INVOICE STATUS
// ========================================

export const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  disputed: 'Disputed',
  written_off: 'Written Off',
  cancelled: 'Cancelled',
  void: 'Void',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
  // UI-only (not in the DB enum) — kept for the contractor app's unpaid filter.
  unpaid: 'Unpaid'
};

export const INVOICE_STATUS_COLORS = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  sent: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  viewed: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  partially_paid: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  paid: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  overdue: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  disputed: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  written_off: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  void: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
  refunded: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
  partially_refunded: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
  unpaid: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' }
};

// ========================================
// INVOICE KIND (invoice_kind_enum)
// ========================================

export const INVOICE_KIND_LABELS = {
  standard: 'Standard',
  deposit: 'Deposit',
  progress: 'Progress',
  final: 'Final'
};

// ========================================
// HELPERS
// ========================================

// Last-resort humanizer for a value missing from the maps above. Replaces EVERY
// underscore — `.replace('_', ' ')` only replaces the first, which is why
// `work_in_progress` used to render as "Work in_progress".
const humanize = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalize = (status) => String(status || '').toLowerCase();

/** Customer/contractor-safe label for a work_order_status_enum value. Never returns a raw token. */
export const getWorkOrderStatusLabel = (status) => {
  if (!status) return 'Unknown';
  const key = normalize(status);
  return WORK_ORDER_STATUS_LABELS[key] || humanize(key);
};

/** Customer/contractor-safe label for an invoice_status_enum value. Never returns a raw token. */
export const getInvoiceStatusLabel = (status) => {
  if (!status) return 'Unknown';
  const key = normalize(status);
  return INVOICE_STATUS_LABELS[key] || humanize(key);
};

/** Label for an invoice_kind_enum value. */
export const getInvoiceKindLabel = (kind) => {
  if (!kind) return '';
  const key = normalize(kind);
  return INVOICE_KIND_LABELS[key] || humanize(key);
};

export const getWorkOrderStatusColor = (status) =>
  WORK_ORDER_STATUS_COLORS[normalize(status)] || WORK_ORDER_STATUS_COLORS.draft;

export const getInvoiceStatusColor = (status) =>
  INVOICE_STATUS_COLORS[normalize(status)] || INVOICE_STATUS_COLORS.unpaid;

// ========================================
// STATUS SETS — who may see it, who may act on it
// ========================================
//
// Added 2026-08-24. Before this, three surfaces each hand-rolled their own answer to
// "is this invoice payable?" and all three disagreed:
//   - customer-portal Invoices.js       said sent OR draft   (offered Pay Now on UNSENT invoices)
//   - customer-portal Portal/ActionsBar said NOT draft/paid/cancelled/void/written_off
//   - contractor-app  InvoicesTable.js  said the same as ActionsBar
// and the portal Dashboard counted `status <> 'paid'` toward "you owe", which billed customers
// for drafts the contractor had never sent ($18,621.60 of draft balance live on 2026-08-24).
//
// These are allow-lists on purpose. A new enum value fails CLOSED (not payable, not counted)
// rather than silently becoming money the customer is told they owe.

/** Invoice statuses a customer may pay. Verified against invoice_status_enum live 2026-08-24. */
export const INVOICE_PAYABLE_STATUSES = ['sent', 'viewed', 'overdue', 'partially_paid'];

/**
 * Invoice statuses a customer may SEE in their portal.
 * Excludes `draft` (not yet issued), `void`/`cancelled` (retracted) and `written_off`
 * (an internal accounting decision — not a customer-facing concept).
 * Superset of INVOICE_PAYABLE_STATUSES: `disputed` and the refund states are shown as
 * history but are not payable.
 */
export const INVOICE_CUSTOMER_VISIBLE_STATUSES = [
  ...INVOICE_PAYABLE_STATUSES,
  'paid',
  'disputed',
  'refunded',
  'partially_refunded'
];

/** True when a customer can pay this invoice now. */
export const isInvoicePayable = (status) =>
  INVOICE_PAYABLE_STATUSES.includes(normalize(status));

/** True when this invoice belongs in a customer's "outstanding balance" total. */
export const invoiceCountsTowardBalance = (status) =>
  INVOICE_PAYABLE_STATUSES.includes(normalize(status));

/** True when this invoice belongs in a customer's invoice list. */
export const isInvoiceCustomerVisible = (status) =>
  INVOICE_CUSTOMER_VISIBLE_STATUSES.includes(normalize(status));

/**
 * Invoice statuses that mean the contractor retracted the invoice. A customer link to one says
 * "voided" and takes no payment (operator ruling 2026-10-24, flow 02 question 1). Voiding sets
 * the totals to 0, so without this a voided invoice read as "already paid".
 */
export const INVOICE_VOIDED_STATUSES = ['void', 'cancelled'];

/** True when the invoice was voided or cancelled. */
export const isInvoiceVoided = (status) =>
  INVOICE_VOIDED_STATUSES.includes(normalize(status));

/**
 * Quote (work_order) statuses on which approve/decline are still open to the customer.
 * Mirrors the guard inside the live `decline_quote_by_token` RPC, verified 2026-08-24:
 *   status IN ('sent','presented','follow_up','changes_requested')
 * `approve_quote_by_token` additionally accepts 'approved' for idempotency, which is a
 * re-submit guard, not a state the Accept button should be offered in.
 */
export const QUOTE_ACTIONABLE_STATUSES = ['sent', 'presented', 'follow_up', 'changes_requested'];

/** Quote statuses a customer may SEE. Excludes `draft` — not yet sent to them. */
export const QUOTE_CUSTOMER_VISIBLE_STATUSES = [
  ...QUOTE_ACTIONABLE_STATUSES,
  'approved',
  'rejected',
  'expired'
];

/** True when the customer can still accept or decline this quote. */
export const isQuoteActionable = (status) =>
  QUOTE_ACTIONABLE_STATUSES.includes(normalize(status));
